import { ComplexPromotionTypes } from '../../graphQL/commons'
import { GetPromotionsUiDetails } from '../../interfaces'
import {
  getItemExtraInfo,
  getPromotionFields,
} from '../common/getPromotionFields'
import formatPrice from '../formatPrice'
import formatPromotionMessage, {
  IFormatPromotionMessage,
} from '../formatPromotionMessage'
import flatTexts from '../flatTexts'

/**
 * Function declaration to get complex promotions ui details to be shown on catalog
 *
 * @internal
 * @param promotion - Selected promotion, should be complex promotion
 * @param config - The config object for get texts
 * @param activePromotion - The active promotion if exists to calculate savings amounts
 * @returns IPromotionUiCatalog for append to catalog product
 */
export const getComplexComboPromotionUiDetails: GetPromotionsUiDetails = ({
  promotion,
  config,
  activePromotion,
}) => {
  const { customerBuysGroups, customerGetsGroups, itemsExtra } =
    getPromotionFields(promotion)

  const {
    comboDifferentDiscountLabel = '',
    comboGetsDiferentProductDescription = '',
    defaultSaving = '',
    defaultPromotionLabel = '',
    complexOneProductGroupAmountLabel = '',
    complexOneProductGroupAmountDescription = '',
  } = flatTexts(config?.texts.promotions)

  const isOneGroupPromotion =
    customerBuysGroups.length === 1 && customerGetsGroups?.length === 1

  if (isOneGroupPromotion) {
    const [{ items: customerBuysItems, type: customerBuysItemType }] =
      customerBuysGroups
    const [{ items: customerGetsItems, type: customerGetsItemType }] =
      customerGetsGroups
    const isOneProductPromotion = customerBuysItems.length === 1
    const isBuyQuantity =
      customerBuysItemType === ComplexPromotionTypes.QUANTITY

    // one group one product use cases
    if (isOneProductPromotion) {
      const [{ quantity: customerBuysQuantity, amount: customerBuysAmount }] =
        customerBuysItems
      const [{ sku: customerGetsItemSku, quantity: customerGetsQuantity }] =
        customerGetsItems

      const [
        itemExtraExists,
        {
          price: itemExtraPrice,
          name: productDescription,
          imageURL: productImage,
        },
      ] = getItemExtraInfo(itemsExtra, customerGetsItemSku)
      const { count: countActivePromotion = 0 } = activePromotion || {}

      const isBuyQuantityGetsQuantity =
        isBuyQuantity && customerGetsItemType === ComplexPromotionTypes.QUANTITY

      const isBuyQuantityGetsPercentageOrDirect =
        isBuyQuantity &&
        (customerGetsItemType === ComplexPromotionTypes.PERCENTAGE ||
          customerGetsItemType === ComplexPromotionTypes.DIRECT_PERCENTAGE)

      const isBuyAmountGetsQuantity =
        customerBuysItemType === ComplexPromotionTypes.AMOUNT &&
        customerGetsItemType === ComplexPromotionTypes.QUANTITY

      if (
        itemExtraExists &&
        (isBuyQuantityGetsQuantity ||
          isBuyAmountGetsQuantity ||
          isBuyQuantityGetsPercentageOrDirect)
      ) {
        const saving =
          itemExtraPrice *
          (customerGetsQuantity || 0) *
          (countActivePromotion || 1)
        const discount = String(
          (customerGetsQuantity || 0) + (customerBuysQuantity || 0),
        )

        const currency: string | undefined =
          config?.options?.currency || config?.options?.currencySymbol
        const format: string | undefined = config?.options?.format

        const customerBuysAmountFormatted = formatPrice({
          currency,
          format,
          price: customerBuysAmount,
          hidePrices: config?.options?.hidePricesActive,
        })

        const formatArgs: IFormatPromotionMessage = {
          message: '',
          promotion,
          config,
          saving,
          placeholders: {
            discount: String(discount),
            value: isBuyAmountGetsQuantity
              ? customerBuysAmountFormatted
              : String(customerBuysQuantity),
            product: productDescription,
            quantity: String(customerGetsQuantity),
          },
        }
        const savingLabel = formatPromotionMessage({
          ...formatArgs,
          message: defaultSaving as string,
        })

        const defaultLabel = (
          isBuyAmountGetsQuantity
            ? complexOneProductGroupAmountLabel
            : comboDifferentDiscountLabel
        ) as string
        const defaultDescription = (
          isBuyAmountGetsQuantity
            ? complexOneProductGroupAmountDescription
            : comboGetsDiferentProductDescription
        ) as string
        const promotionLabelMessage = promotion.name || defaultLabel
        const promotionDescriptionMessage =
          promotion.description || defaultDescription
        const label = formatPromotionMessage({
          ...formatArgs,
          message: promotionLabelMessage,
        })
        const description = formatPromotionMessage({
          ...formatArgs,
          message: promotionDescriptionMessage,
        })

        return {
          data: promotion,
          title: defaultPromotionLabel,
          label,
          description,
          saving: promotion.name ? '' : savingLabel,
          image: productImage,
        }
      }
    }

    return {
      data: promotion,
      title: defaultPromotionLabel,
      label: defaultPromotionLabel,
      description: '',
    }
  }

  return {
    data: promotion,
    label: defaultPromotionLabel,
    title: defaultPromotionLabel,
    description: '',
  }
}
